/* E11 prospective measurement adapter. Production publication stays disabled by default.
   Builder X item 1 (Master, 3 Oct 2026): a published event also goes to the store's GA4 property (the gtag the Google
   channel already puts on the page), and internal traffic is excluded: automation (navigator.webdriver) and any browser
   marked once with ?upsb_internal=1 (cleared with ?upsb_internal=0). */
(function (root) {
  'use strict';
  var MAX = 10000;
  var EVENTS = {
    upsb_builder_viewed: { entry: ['default', 'recovered'] },
    upsb_requirements_committed: { origin: ['buyer', 'preset', 'recovery'], preset: ['closet', 'servers', 'school', 'lab', 'room', 'three', 'describe'] },
    upsb_results_viewed: { mode: ['results', 'technical_review', 'specialist'], visible_count: 'count', eligible_count: 'count', review_count: 'count', mismatch_count: 'count' },
    upsb_quote_list_changed: { action: ['add', 'remove', 'quantity', 'clear', 'update'], row_count: 'count', storage: ['persisted', 'failed'] },
    upsb_quote_handoff: { result: ['initiated', 'blocked'], reason: 'reason' },
    upsb_quote_receiver_viewed: { result: ['ready', 'repair', 'missing', 'server_error'], route: ['short', 'reference', 'recovered', 'direct'] },
    upsb_cart_result: { result: ['success', 'error', 'price_changed', 'blocked'], reason: 'reason', line_count: 'count' },
    upsb_quote_ack_viewed: {},
    // Lane Y (2 Oct 2026): cross-brand alternatives on product pages (surface 'product' only).
    upsb_alt_opened: { trigger: ['auto', 'button'], page: ['discontinued', 'current'], shown_count: 'count' },
    upsb_alt_closed: { reason: ['close', 'keep', 'backdrop', 'escape'], page: ['discontinued', 'current'] },
    upsb_alt_clicked: { action: ['view', 'cart', 'quote', 'freight'], placement: ['dialog', 'inline'],
      page: ['discontinued', 'current'], position: 'count' }
  };
  var REQUIRED = {
    upsb_builder_viewed: ['entry'], upsb_requirements_committed: ['origin'],
    upsb_results_viewed: ['mode', 'visible_count', 'eligible_count', 'review_count', 'mismatch_count'],
    upsb_quote_list_changed: ['action', 'row_count', 'storage'], upsb_quote_handoff: ['result'],
    upsb_quote_receiver_viewed: ['result', 'route'], upsb_cart_result: ['result'], upsb_quote_ack_viewed: [],
    upsb_alt_opened: ['trigger', 'page', 'shown_count'], upsb_alt_closed: ['reason', 'page'],
    upsb_alt_clicked: ['action', 'placement', 'page', 'position']
  };
  // 16 Oct 2026 (code audit #20): the Builder also blocks a cart add for capacity_review, j60_source_review,
  // input_voltage_review and variant_ambiguous; the event was dropped while those were missing here.
  var REASONS = ['invalid_requirements', 'invalid_saved', 'invalid_quantity', 'requirements_not_saved',
    'saved_review', 'quote_only', 'configuration_changed', 'missing_product', 'outlet', 'runtime',
    'required_component', 'physical_fit', 'price_changed', 'transport', 'storage', 'request_failed', 'unknown',
    'capacity_review', 'j60_source_review', 'input_voltage_review', 'variant_ambiguous'];
  var LOCALES = ['en', 'fr'], SURFACES = ['builder', 'quote_receiver', 'product'], VIEWPORTS = ['compact', 'wide'];
  function valid(value, spec) {
    if (spec === 'count') return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= MAX;
    if (spec === 'reason') return REASONS.indexOf(value) !== -1;
    return typeof value === 'string' && spec.indexOf(value) !== -1;
  }
  function buildPayload(name, fields, ctx) {
    var spec = EVENTS[name];
    if (!spec || !fields || typeof fields !== 'object' || Array.isArray(fields) || !ctx ||
        LOCALES.indexOf(ctx.locale) < 0 || SURFACES.indexOf(ctx.surface) < 0 ||
        (ctx.viewportClass && VIEWPORTS.indexOf(ctx.viewportClass) < 0)) return null;
    if ((name === 'upsb_quote_receiver_viewed' || name === 'upsb_quote_ack_viewed') !== (ctx.surface === 'quote_receiver')) return null;
    if ((name.indexOf('upsb_alt_') === 0) !== (ctx.surface === 'product')) return null;
    var out = { schema_version: 1, locale: ctx.locale, surface: ctx.surface };
    if (ctx.viewportClass) out.viewport_class = ctx.viewportClass;
    Object.keys(fields).forEach(function (key) {
      if (!Object.prototype.hasOwnProperty.call(spec, key) || !valid(fields[key], spec[key])) out = null;
      else if (out) out[key] = fields[key];
    });
    if (!out || REQUIRED[name].some(function (key) { return !Object.prototype.hasOwnProperty.call(out, key); })) return null;
    if (name === 'upsb_requirements_committed' && (out.preset ? out.origin !== 'preset' : out.origin === 'preset')) return null;
    if (name === 'upsb_results_viewed' && (out.eligible_count + out.review_count + out.mismatch_count !== out.visible_count ||
        (out.mode !== 'results' && out.visible_count !== 0))) return null;
    if (name === 'upsb_quote_handoff' && ((out.result === 'blocked') !== !!out.reason)) return null;
    if (name === 'upsb_cart_result') {
      var allowed = { success: [], error: ['request_failed'], price_changed: ['price_changed'],
        blocked: ['invalid_requirements', 'invalid_saved', 'saved_review', 'quote_only', 'configuration_changed',
          'missing_product', 'outlet', 'runtime', 'required_component', 'physical_fit', 'unknown',
          'capacity_review', 'j60_source_review', 'input_voltage_review', 'variant_ambiguous'] };
      if (allowed[out.result].indexOf(out.reason) < 0 && !(out.result === 'success' && !out.reason)) return null;
      if (out.line_count != null && out.result !== 'success') return null;
    }
    return out;
  }
  // The GA4 measurement id comes from the page's own gtag config (G-...), never hard-coded here.
  function toGa4(name, payload) {
    try {
      if (typeof root.gtag !== 'function' || !root.dataLayer || !root.dataLayer.length) return;
      var id = '';
      for (var i = 0; i < root.dataLayer.length && !id; i++) {
        var d = root.dataLayer[i];
        if (d && d[0] === 'config' && typeof d[1] === 'string' && /^G-[A-Z0-9]+$/.test(d[1])) id = d[1];
      }
      if (!id) return;
      var params = { send_to: id };
      Object.keys(payload).forEach(function (k) { params[k] = payload[k]; });
      root.gtag('event', name, params);
    } catch (e) { /* analytics never breaks the page */ }
  }
  function internalMarked(query) {
    try {
      if (root.navigator && root.navigator.webdriver === true) return true;
      var ls = root.localStorage;
      if (query && query.get && query.get('upsb_internal') === '1') ls.setItem('upsb_internal', '1');
      if (query && query.get && query.get('upsb_internal') === '0') ls.removeItem('upsb_internal');
      return ls.getItem('upsb_internal') === '1';
    } catch (e) { return false; }
  }
  function create(opts) {
    opts = opts || {};
    var c = opts.context || {};
    var ctx = { locale: c.locale, surface: c.surface, viewportClass: c.viewportClass };
    var sink = typeof opts.sink === 'function' ? opts.sink : null;
    var seen = {};
    // Builder X (Master, 3 Oct 2026): the builder's first events fire about 20 ms before Shopify's consent API and about
    // 300 ms before its analytics publisher exist (measured on the live builder), so they were dropped. An event that
    // finds either API missing waits (at most 20 events, retried every 500 ms for 20 s) and is then checked as usual.
    var pending = [], tries = 0, waiting = false;
    function later(args) {
      if (pending.length < 20) pending.push(args);
      if (waiting || typeof root.setTimeout !== 'function') return;
      waiting = true;
      root.setTimeout(flush, 500);
    }
    function flush() {
      waiting = false; tries++;
      var list = pending; pending = [];
      list.forEach(function (a) { emit(a[0], a[1], a[2]); });
      if (pending.length && tries < 40) { waiting = true; root.setTimeout(flush, 500); }
      else if (tries >= 40) pending = [];
    }
    function privacyLoaded() {
      return typeof opts.privacy === 'function' || !!(root.Shopify && root.Shopify.customerPrivacy &&
        typeof root.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function');
    }
    try {
      if (!opts.privacy && root.Shopify && typeof root.Shopify.loadFeatures === 'function')
        root.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () {});
    } catch (e) { /* Missing privacy API suppresses until a later action can check it. */ }
    function consent() {
      try {
        var p = typeof opts.privacy === 'function' ? opts.privacy() : root.Shopify && root.Shopify.customerPrivacy;
        return !!(p && typeof p.analyticsProcessingAllowed === 'function' && p.analyticsProcessingAllowed() === true);
      } catch (e) { return false; }
    }
    function productionAllowed() {
      var expected = c.expectedThemeId, rendered = c.renderedThemeId, observed = c.observedThemeId;
      return c.releaseEnabled === true && typeof expected === 'string' && /^[0-9]+$/.test(expected) &&
        expected === rendered && expected === observed && c.renderedRole === 'main' && c.observedRole === 'main' &&
        c.designMode === false && c.internalTest === false && c.preview === false;
    }
    function emit(name, fields, key) {
      try {
        var payload = buildPayload(name, fields, ctx);
        if (!payload) return { attempted: false, captured: false, reason: 'payload' };
        if (!consent()) {
          if (!sink && !privacyLoaded()) { later([name, fields, key]); return { attempted: false, captured: false, reason: 'pending' }; }
          return { attempted: false, captured: false, reason: 'consent' };
        }
        if (!sink && !productionAllowed()) return { attempted: false, captured: false, reason: 'suppressed' };
        var dedupe = key == null ? null : name + ':' + String(key);
        if (dedupe && seen[dedupe]) return { attempted: false, captured: false, reason: 'duplicate' };
        if (sink) {
          sink(name, payload);
          if (dedupe) seen[dedupe] = true;
          return { attempted: true, captured: true, reason: 'local_sink' };
        }
        var publish = typeof opts.publish === 'function' ? opts.publish :
          root.Shopify && root.Shopify.analytics && typeof root.Shopify.analytics.publish === 'function' ?
            function (n, data) { return root.Shopify.analytics.publish(n, data); } : null;
        if (typeof publish !== 'function') { later([name, fields, key]); return { attempted: false, captured: false, reason: 'no_publisher' }; }
        var result = publish(name, payload);
        if (result && typeof result.then === 'function') result.then(null, function () {});
        if (!opts.publish) toGa4(name, payload);
        if (dedupe) seen[dedupe] = true;
        return { attempted: true, captured: false, reason: 'publisher_invoked' };
      } catch (e) { return { attempted: false, captured: false, reason: 'error' }; }
    }
    return { emit: emit };
  }
  function forElement(el, surface, locale) {
    var shop = root.Shopify || {}, theme = shop.theme || {};
    var query;
    try { query = new URLSearchParams(root.location.search); } catch (e) { query = { has: function () { return true; } }; }
    return create({
      context: {
        locale: locale, surface: surface, viewportClass: root.innerWidth <= 767 ? 'compact' : 'wide',
        releaseEnabled: el.getAttribute('data-measure-release') === 'true',
        expectedThemeId: el.getAttribute('data-measure-expected-theme-id') || '',
        renderedThemeId: el.getAttribute('data-measure-theme-id') || '',
        observedThemeId: theme.id == null ? '' : String(theme.id),
        renderedRole: el.getAttribute('data-measure-role') || '', observedRole: theme.role || '',
        // Liquid request.design_mode must render 'false'; Shopify.designMode is set (true) only inside the theme editor and
        // is undefined on the storefront, so only an explicit true counts as design mode (live finding 2 Oct 2026).
        designMode: el.getAttribute('data-measure-design-mode') === 'false' && shop.designMode !== true ? false : true,
        internalTest: el.getAttribute('data-measure-internal-test') === 'false' && !query.has('e11_test') && !internalMarked(query) ? false : true,
        preview: query.has('preview_theme_id') || query.has('shopify_preview_theme_id')
      },
      // Deliberately injected only in local fixtures or a separately granted unpublished QA run.
      sink: typeof root.UPSB_E11_LOCAL_SINK === 'function' ? root.UPSB_E11_LOCAL_SINK : null
    });
  }
  root.UPSBMeasure = { create: create, forElement: forElement, buildPayload: buildPayload, EVENTS: EVENTS, REASONS: REASONS };
})(typeof window !== 'undefined' ? window : globalThis);
